import SourceEvidenceReview from './pages/SourceEvidenceReview';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, useClerk } from '@clerk/clerk-react';
import ManageSchools from './pages/ManageSchools';
import ManageUsers from './pages/ManageUsers';
import ManageAchievements from './pages/ManageAchievements';
import Engagement from './pages/Engagement';
import Organizations from './pages/Organizations';
import AiUsage from './pages/AiUsage';
import ContentReview from './pages/ContentReview';
import SchoolDirectory from './pages/SchoolDirectory';
import SignIn from './pages/SignIn';
import ProtectedRoute from './components/ProtectedRoute';
import IdleLogoutWatcher from './components/IdleLogoutWatcher';
import EnvironmentBanner from './components/EnvironmentBanner';
import { setAccessTokenGetter, setSignOutFunction } from './services/apiFetch';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/** Bridges Clerk's auth hooks into the module-level apiFetch utility. */
function AuthTokenSetter() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  useEffect(() => {
    setAccessTokenGetter(() => getToken());
    setSignOutFunction((opts) => signOut(opts));
  }, [getToken, signOut]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthTokenSetter />
      <IdleLogoutWatcher />
      {/* Renders nothing on production; badges every other host. */}
      <EnvironmentBanner />
      <Routes>
        <Route path="/source-reviews" element={<ProtectedRoute><SourceEvidenceReview /></ProtectedRoute>} />
        {/* Wildcard so Clerk's <SignIn> can own its own subpaths (OAuth
            SSO callback lives at /signin/sso-callback, etc.). */}
        <Route path="/signin/*" element={<SignIn />} />
        {/* Compat: students-app-style /auth/signin path → redirect */}
        <Route path="/auth/signin" element={<Navigate to="/signin" replace />} />
        {/* Compat: legacy Logto OAuth callback → just send to /signin (Clerk
            handles its own callback inline; nothing should be hitting this
            path post-migration but keep the redirect so old bookmarks work). */}
        <Route path="/callback" element={<Navigate to="/signin" replace />} />
        <Route
          path="/manage-schools"
          element={
            <ProtectedRoute>
              <ManageSchools />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manage-users"
          element={
            <ProtectedRoute>
              <ManageUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manage-achievements"
          element={
            <ProtectedRoute>
              <ManageAchievements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizations"
          element={
            <ProtectedRoute>
              <Organizations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-usage"
          element={
            <ProtectedRoute>
              <AiUsage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/engagement"
          element={
            <ProtectedRoute>
              <Engagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content-review"
          element={
            <ProtectedRoute>
              <ContentReview />
            </ProtectedRoute>
          }
        />
        {/* Compat: old onboarding-stats bookmark → engagement */}
        <Route path="/onboarding-stats" element={<Navigate to="/engagement" replace />} />
        <Route
          path="/school-directory"
          element={
            <ProtectedRoute>
              <SchoolDirectory />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/manage-schools" replace />} />
        {/* Catch-all → send unknown paths to the sign-in surface */}
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </BrowserRouter>
  );
}

export default App;
